import { Injectable } from '@angular/core'
import { CaseDisplay, CaseSummary } from '@ftr/annotations-contracts'
import { CaseResponse, CaseUpdateRequest } from '@ftr/annotations-contracts/v2'
import { ConflictApiError, NotFoundApiError, performTsRestApiRequest, UnauthorizedApiError } from '@ftr/api-shared'
import { PagedResult } from '@ftr/contracts/api/core'
import { Department } from '@ftr/contracts/api/department'
import { SortDirection } from '@ftr/contracts/api/shared'
import { Uuid } from '@ftr/contracts/type/shared'
import { CourtSystemRegionCache } from '@ftr/data-court-system'
import { RealTimePlaybackKey } from '@ftr/data-realtime-playback'
import { ApiResult, RemoteData } from '@ftr/foundation'
import { keyBy } from 'lodash-es'
import { firstValueFrom } from 'rxjs'
import { AnnotationsApiClientFactory, AnnotationsApiClientFactoryV2 } from '../../api-client'
import { BadRequestApiError } from '../../api-client/errors'
import { AnnotationsService } from '../base/annotations.service'

@Injectable({
  providedIn: 'root',
})
export class CasesService extends AnnotationsService {
  constructor(
    apiClientFactory: AnnotationsApiClientFactory, // v1 for existing methods
    regionCache: CourtSystemRegionCache,
    apiClientFactoryV2: AnnotationsApiClientFactoryV2, // v2 for new methods
  ) {
    super(apiClientFactory, regionCache, apiClientFactoryV2)
  }

  getCasesPaginated(options: {
    courtSystemId: string
    page: number
    pageSize: number
    sortDirection: SortDirection
  }): ApiResult<PagedResult<CaseSummary>> {
    const { courtSystemId, page, pageSize, sortDirection } = options

    return this.performApiRequest({
      courtSystemId,
      requestFn: client =>
        client.cases.getCases({
          query: {
            courtSystemId,
            limit: pageSize,
            offset: (page - 1) * pageSize,
            sortDirection,
          },
        }),
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.failure(new Error(result.body.error))
        }

        return RemoteData.success<PagedResult<CaseSummary>>({
          items: result.body.items,
          meta: { totalItems: result.body.total, number: page, size: pageSize },
        })
      },
    })
  }

  getCasesByLocation({
    courtSystemId,
    locationId,
    page = 1,
    pageSize = 10,
    sortDirection = SortDirection.Descending,
  }: {
    courtSystemId: Uuid
    locationId: Uuid
    page?: number
    pageSize?: number
    sortDirection?: SortDirection
  }): ApiResult<PagedResult<CaseSummary>> {
    return this.performApiRequest({
      courtSystemId,
      requestFn: client =>
        client.cases.getCasesForLocation({
          query: {
            courtSystemId,
            locationId,
            limit: pageSize,
            offset: (page - 1) * pageSize,
            sortDirection,
          },
        }),
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.failure(new Error(result.body?.error))
        }

        return RemoteData.success<PagedResult<CaseSummary>>({
          items: result.body.items,
          meta: { totalItems: result.body.total, number: page, size: pageSize },
        })
      },
    })
  }

  async getCaseDisplaysForPlayback(
    playbackKey: RealTimePlaybackKey,
    courtSystemId: Uuid,
  ): Promise<Record<string, CaseDisplay>> {
    const client = await firstValueFrom(this.getApiClient(courtSystemId))
    const playbackRequiringCases: RealTimePlaybackKey['type'][] = ['recording', 'audio-segment']
    const recordingId = playbackKey.recordingId
    if (playbackRequiringCases.includes(playbackKey.type) && recordingId) {
      const casesResult = await client.cases.getCasesForRecording({
        query: {
          recordingId,
          courtSystemId,
          audioSegmentId: playbackKey.type === 'audio-segment' ? playbackKey.audioSegmentId : undefined,
        },
      })

      if (casesResult.status === 200) {
        return keyBy(casesResult.body, 'id')
      }
    }
    return {}
  }

  getCase({ caseId, courtSystemId }: { caseId: Uuid; courtSystemId: Uuid }): ApiResult<CaseResponse> {
    return performTsRestApiRequest(this.getApiClientV2(courtSystemId), {
      requestFn: client => client.cases.getCase({ params: { courtSystemId, caseId } }),
      responseMapFn: result => {
        if (result.status === 404) {
          return RemoteData.failure(new NotFoundApiError())
        }
        if (result.status === 401 || result.status === 403) {
          return RemoteData.failure(new UnauthorizedApiError())
        }
        if (result.status === 400) {
          return RemoteData.failure(new BadRequestApiError())
        }

        return RemoteData.success(result.body)
      },
    })
  }

  updateCase({
    caseId,
    courtSystemId,
    caseUpdate,
  }: {
    caseId: Uuid
    courtSystemId: Uuid
    caseUpdate: CaseUpdateRequest
  }): ApiResult<CaseResponse> {
    return performTsRestApiRequest(this.getApiClientV2(courtSystemId), {
      requestFn: client => client.cases.updateCase({ params: { courtSystemId, caseId }, body: caseUpdate }),
      responseMapFn: result => {
        if (result.status === 409) {
          return RemoteData.failure(new ConflictApiError())
        }
        if (result.status === 404) {
          return RemoteData.failure(new NotFoundApiError())
        }
        if (result.status === 401 || result.status === 403) {
          return RemoteData.failure(new UnauthorizedApiError())
        }
        if (result.status === 400) {
          return RemoteData.failure(new BadRequestApiError())
        }

        return RemoteData.success(result.body)
      },
    })
  }

  getCaseDepartments({
    caseId,
    courtSystemId,
  }: {
    caseId: Uuid
    courtSystemId: Uuid
  }): ApiResult<{ departments?: Pick<Department, 'id'>[] | null }> {
    return performTsRestApiRequest(this.getApiClient(courtSystemId), {
      requestFn: client => client.case.getDepartmentsForCase({ params: { caseId } }),
      responseMapFn: result => {
        if (result.status === 404) {
          return RemoteData.failure(new NotFoundApiError())
        }
        if (result.status === 401 || result.status === 403) {
          return RemoteData.failure(new UnauthorizedApiError())
        }

        if (result.status === 200) {
          return RemoteData.success(result.body)
        }

        return RemoteData.failure(new Error('Unable to fetch case departments'))
      },
    })
  }
}
